/**
 * Trunnion AI - Execution Heatmap
 * 7x24 grid showing execution density by day of week and hour.
 */

import { useMemo, useState } from "react";
import { Calendar } from "lucide-react";
import { Card, CardHeader, CardTitle, CardContent } from "@/components/ui/Card";

const DAYS = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];
const HOUR_LABELS = [0, 6, 12, 18];

interface HeatmapCell {
  day: number;
  hour: number;
  count: number;
}

interface TooltipData {
  day: string;
  hour: number;
  count: number;
  x: number;
  y: number;
}

function generateMockHeatmapData(): HeatmapCell[] {
  const cells: HeatmapCell[] = [];
  for (let day = 0; day < 7; day++) {
    for (let hour = 0; hour < 24; hour++) {
      // Simulate more activity during work hours on weekdays
      const isWeekday = day < 5;
      const isWorkHour = hour >= 8 && hour <= 18;
      const baseProbability = isWeekday ? (isWorkHour ? 0.7 : 0.2) : 0.1;
      const count =
        Math.random() < baseProbability
          ? Math.floor(Math.random() * (isWorkHour ? 30 : 10))
          : 0;
      cells.push({ day, hour, count });
    }
  }
  return cells;
}

function getIntensityClass(count: number, max: number): string {
  if (count === 0) return "bg-trunnion-slate-800";
  const ratio = count / max;
  if (ratio < 0.2) return "bg-trunnion-gold-500/20";
  if (ratio < 0.4) return "bg-trunnion-gold-500/40";
  if (ratio < 0.7) return "bg-trunnion-gold-500/70";
  return "bg-trunnion-gold-500";
}

export function ExecutionHeatmap() {
  const [tooltip, setTooltip] = useState<TooltipData | null>(null);

  const data = useMemo(() => generateMockHeatmapData(), []);

  const maxCount = useMemo(
    () => Math.max(...data.map((c) => c.count), 1),
    [data],
  );

  // Build a lookup grid: data[day][hour]
  const grid = useMemo(() => {
    const g: number[][] = Array.from({ length: 7 }, () =>
      Array.from({ length: 24 }, () => 0),
    );
    for (const cell of data) {
      g[cell.day][cell.hour] = cell.count;
    }
    return g;
  }, [data]);

  return (
    <Card padding="none">
      <CardHeader>
        <div className="flex items-center justify-between">
          <CardTitle>
            <Calendar className="h-4 w-4 inline mr-1.5 text-trunnion-gold-500" />
            Execution Activity
          </CardTitle>
          <div className="flex items-center gap-1.5 text-[10px] text-trunnion-slate-500">
            <span>Less</span>
            <span className="h-3 w-3 rounded-sm bg-trunnion-slate-800" />
            <span className="h-3 w-3 rounded-sm bg-trunnion-gold-500/20" />
            <span className="h-3 w-3 rounded-sm bg-trunnion-gold-500/40" />
            <span className="h-3 w-3 rounded-sm bg-trunnion-gold-500/70" />
            <span className="h-3 w-3 rounded-sm bg-trunnion-gold-500" />
            <span>More</span>
          </div>
        </div>
      </CardHeader>
      <CardContent>
        <div className="relative overflow-x-auto">
          {/* Hour labels */}
          <div className="flex ml-10 mb-1">
            {Array.from({ length: 24 }, (_, i) => (
              <div
                key={i}
                className="w-4 shrink-0 text-center"
                style={{ marginRight: "2px" }}
              >
                {HOUR_LABELS.includes(i) ? (
                  <span className="text-[9px] text-trunnion-slate-500">
                    {i}
                  </span>
                ) : null}
              </div>
            ))}
          </div>

          {/* Grid rows */}
          {DAYS.map((dayLabel, dayIndex) => (
            <div key={dayLabel} className="flex items-center mb-0.5">
              {/* Day label */}
              <span className="w-10 text-[10px] text-trunnion-slate-500 shrink-0 text-right pr-2">
                {dayLabel}
              </span>
              {/* Hour cells */}
              <div className="flex gap-0.5">
                {grid[dayIndex].map((count, hour) => (
                  <div
                    key={hour}
                    className={`w-4 h-4 rounded-sm cursor-pointer transition-colors ${getIntensityClass(count, maxCount)}`}
                    onMouseEnter={(e) => {
                      const rect = e.currentTarget.getBoundingClientRect();
                      const parent = e.currentTarget
                        .closest(".relative")
                        ?.getBoundingClientRect();
                      setTooltip({
                        day: DAYS[dayIndex],
                        hour,
                        count,
                        x: rect.left - (parent?.left ?? 0) + rect.width / 2,
                        y: rect.top - (parent?.top ?? 0) - 8,
                      });
                    }}
                    onMouseLeave={() => setTooltip(null)}
                  />
                ))}
              </div>
            </div>
          ))}

          {/* Tooltip */}
          {tooltip && (
            <div
              className="absolute z-10 pointer-events-none rounded-lg border border-trunnion-slate-700 bg-trunnion-slate-900 px-2.5 py-1.5 text-xs shadow-lg"
              style={{
                left: tooltip.x,
                top: tooltip.y,
                transform: "translate(-50%, -100%)",
              }}
            >
              <p className="text-white font-medium">
                {tooltip.day} {String(tooltip.hour).padStart(2, "0")}:00
              </p>
              <p className="text-trunnion-slate-400">
                {tooltip.count} execution{tooltip.count !== 1 ? "s" : ""}
              </p>
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );
}
